import { Module } from "@/constants";
import { useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";

// Refresh just this module: other offline-first queries keep their existing policy.
export const usePlannedDurationVisibilityClock = (
  validUntil?: string | null,
) => {
  const queryClient = useQueryClient();
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    let timer: number;
    let day = DateTime.now().setZone("Europe/Warsaw").toISODate();
    const deadline = validUntil ? Date.parse(validUntil) : undefined;
    const schedule = () => {
      window.clearTimeout(timer);
      const instant = Date.now();
      const midnight = DateTime.fromMillis(instant)
        .setZone("Europe/Warsaw")
        .plus({ days: 1 })
        .startOf("day")
        .toMillis();
      const next =
        deadline !== undefined && deadline > instant
          ? Math.min(deadline, midnight)
          : midnight;
      timer = window.setTimeout(check, Math.max(1, next - instant));
    };
    const check = () => {
      const instant = Date.now();
      const currentDay = DateTime.fromMillis(instant)
        .setZone("Europe/Warsaw")
        .toISODate();
      const expired =
        deadline !== undefined &&
        (!Number.isFinite(deadline) || instant >= deadline);
      setNow(instant);
      if (currentDay !== day || expired) {
        day = currentDay;
        void queryClient.invalidateQueries({
          queryKey: [Module.ContractorPanelVisits],
        });
      }
      schedule();
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };
    check();
    window.addEventListener("focus", check);
    window.addEventListener("online", check);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", check);
      window.removeEventListener("online", check);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [queryClient, validUntil]);

  return now;
};
